/* eslint-disable object-shorthand */
import React, { useEffect, useState } from "react";
import { Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import moment from "moment";
import { useParams } from "react-router-dom/cjs/react-router-dom";
import { useAuth } from "../../../../contexts/AuthContext";
import { useStructure } from "../../../../contexts/StructureContext";
import PeriodSelector from "../../../global/PeriodSelector";
import { RecursiveStructureReport } from "./RecursiveStructureReport";
import * as API from "../../../../api";

/**
 * The Structure Report component displays a table of order statistics for the current user's
 * downstream agents. The table shows the dealer code, tier 1, assistant tier leader, agent
 * code and name, created orders, paid orders, installed orders, and activated orders. The
 * component fetches the structure report data from the server and displays it in the table.
 * The component also allows the user to download the structure report as a CSV file.
 * @param {object} props - The component's props.
 */
export default function StructureReport(props) {
  const { currentUser } = useAuth();
  // eslint-disable-next-line
  const { downstream, downstreamIDs } = useStructure();
  const [dealerCode, setDealerCode] = useState(currentUser.id);
  const [dealerId, setDealerId] = useState(undefined);
  const { supplierPrettyName, impersonation, impersonationName } = props;
  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    moment().endOf("month").format("YYYY-MM-DD")
  );
  const [showLoading, setShowLoading] = useState(false);
  const [dynamicDownstream, setDynamicDownstream] = useState([]);
  const { supplierId } = useParams();
  const [error, setError] = useState(false);
  const [dealerTree, setDealerTree] = useState([]);
  const lowestLevel = 0;

  const fetchStructure = async id => {
    if (!id) return;
    try {
      setShowLoading(true);
      setError(false);
      const dealerStructure = await API.getStructureOrderReport({
        user_id: id,
        supplier_id: !supplierId ? "" : supplierId,
        start_date: startDate,
        end_date: endDate,
        is_admin: impersonationName ? false : currentUser.is_admin,
        downstream_ids: impersonationName ? [] : downstreamIDs,
      });
      setDynamicDownstream(dealerStructure.data);
    } catch (err) {
      console.error("Error fetching structure:", err);
      setError(true);
    } finally {
      setShowLoading(false);
    }
  };

  useEffect(() => {
    if (!dealerId || !startDate || !endDate) return;

    const fetchData = async () => {
      setDynamicDownstream([]);
      setDealerTree([]);
      await fetchStructure(dealerId);
    };

    fetchData();
  }, [dealerId, startDate, endDate, supplierId]);

  /**
   * Builds a nested tree structure from a flat array of objects.
   * @param {object[]} flatArray - The flat array of objects.
   * @returns {object[]} The nested tree structure.
   * @private
   */
  function buildTreeArray(flatArray) {
    if (!flatArray || flatArray.length === 0) return [];
    if (
      !flatArray?.length > 0 ||
      flatArray[0]?.id === undefined ||
      flatArray[0]?.id === null
    ) {
      setError(true);
      setShowLoading(false);
      return [];
    }
    setError(false);
    const nodeMap = flatArray.reduce((acc, item) => {
      acc[item?.id] = {
        ...item,
        name: `Z-${item.id} ${item.first_name} ${item.surname}`,
        children: [],
        level: null,
        total: {
          created: Number(item?.created) || 0,
          paid: Number(item?.paid) || 0,
          installed: Number(item?.installed) || 0,
          activated: Number(item?.activated) || 0,
        },
      };
      return acc;
    }, {});

    const rootNodes = [];

    flatArray.forEach(item => {
      const parentNode = nodeMap[item.parent_id];
      if (parentNode) {
        parentNode.children.push(nodeMap[item.id]);
      } else {
        const rootUser = nodeMap[item.id];

        const displayCrit =
          rootUser.parent_id === null ||
          rootUser.created !== "0" ||
          rootUser.paid !== "0" ||
          rootUser.installed !== "0" ||
          rootUser.activated !== "0" ||
          flatArray.some(child => child.parent_id === rootUser.id);

        if (displayCrit) {
          rootNodes.push(nodeMap[item.id]);
        }
      }
    });

    /**
     * Assigns a level to each node in the tree structure.
     * The level is determined by the position of the node in the tree,
     * starting from the root node with level 1.
     * @param {object} currentNode - The current node in the tree.
     * @param {number} currentLevel - The current level of the node.
     * @returns {object} The updated node with the assigned level.
     */
    function assignLevels(currentNode, currentLevel) {
      // Initialize the updated node with the current level
      const updatedNode = {
        ...currentNode,
        level: currentLevel,
      };

      updatedNode.children = updatedNode.children.map(child =>
        assignLevels(child, currentLevel + 1)
      );

      const totalPaid = updatedNode.children.reduce(
        (sum, child) => sum + child.total.paid,
        0
      );
      const totalCreated = updatedNode.children.reduce(
        (sum, child) => sum + child.total.created,
        0
      );
      const totalInstalled = updatedNode.children.reduce(
        (sum, child) => sum + child.total.installed,
        0
      );
      const totalActivated = updatedNode.children.reduce(
        (sum, child) => sum + child.total.activated,
        0
      );

      // Add the children's total paid value to the parent's paid total
      updatedNode.total.paid += totalPaid;
      updatedNode.total.created += totalCreated;
      updatedNode.total.installed += totalInstalled;
      updatedNode.total.activated += totalActivated;

      return updatedNode;
    }

    return rootNodes.map(rootNode =>
      assignLevels(rootNode, lowestLevel > 0 ? lowestLevel + 1 : 1)
    );
  }

  useEffect(() => {
    if (dynamicDownstream.length > 0) {
      const dealerTreeData = buildTreeArray(dynamicDownstream);
      setDealerTree(dealerTreeData);
    }
  }, [dynamicDownstream]);

  useEffect(() => {
    if (dealerTree.length > 0 && error === false) {
      setShowLoading(false);
    }
  }, []);

  useEffect(() => {
    // Update dealer code and id based on impersonation or current user
    if (impersonation) {
      setDealerCode(impersonation);
      const impersonationId = downstream?.filter(
        agent => agent.id === impersonation
      );
      setDealerId(impersonationId[0].id);
    } else {
      setDealerId(currentUser.id);
    }
  }, [impersonation]);

  const structureTotals = dealerTree.reduce(
    (sums, obj) => ({
      created: sums.created + (obj.total?.created || 0),
      paid: sums.paid + (obj.total?.paid || 0),
      installed: sums.installed + (obj.total?.installed || 0),
      activated: sums.activated + (obj.total?.activated || 0),
    }),
    { created: 0, paid: 0, installed: 0, activated: 0 }
  );

  return (
    <div>
      <Typography variant="h6">
        Structure Report for{" "}
        {impersonationName ? `: ${impersonationName}` : `: ${dealerCode} (You)`}
      </Typography>
      <Typography variant="h6">Service: {supplierPrettyName}</Typography>
      <div>
        <p>
          Current Period:{" "}
          <b>
            {startDate} - {endDate}
          </b>
        </p>
      </div>
      <PeriodSelector
        changeStartDate={date => {
          setStartDate(date);
        }}
        changeEndDate={date => {
          setEndDate(date);
        }}
      />
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            maxWidth: 1300,
          }}
        >
          {error ? (
            <Alert severity="error">
              Sorry for the inconvenience but there was an error fetching the
              data please contact support if it happens again
            </Alert>
          ) : dynamicDownstream.length > 0 && showLoading === false ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <Alert severity="warning">
                Please note that the Structure Report is currently in testing.
                We’d appreciate your feedback if you encounter any issues.
              </Alert>
              <div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                  }}
                >
                  {/* Render the order statistics */}
                  {[
                    {
                      label: "Created",
                      value: structureTotals.created,
                    },
                    {
                      label: "Paid",
                      value: structureTotals.paid,
                    },
                  ].map(item => (
                    <div
                      style={{
                        marginRight: "4%",
                        textAlign: "center",
                        fontSize: "16px",
                        fontWeight: "450",
                        color: "slategray",
                      }}
                      key={item.label}
                    >
                      <p>{item.label}</p>
                      <p
                        style={{
                          fontSize: "35px",
                          marginTop: -10,
                          color: "slateGray",
                        }}
                      >
                        {item.value}
                      </p>
                    </div>
                  ))}
                  {[
                    {
                      label: "Installed",
                      value: structureTotals.installed,
                    },
                    {
                      label: "Activated",
                      value: structureTotals.activated,
                    },
                  ].map(item => (
                    <div
                      style={{
                        marginRight: "4%",
                        textAlign: "center",
                        fontSize: "16px",
                        fontWeight: "450",
                        color: "slategray",
                      }}
                      key={item.label}
                    >
                      <p>{item.label}</p>
                      <p
                        style={{
                          fontSize: "35px",
                          marginTop: -10,
                          color: "slateGray",
                        }}
                      >
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>{" "}
              <RecursiveStructureReport nodeData={dealerTree} />
            </div>
          ) : showLoading === true ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 300,
                fontSize: "24px", // Add this line to enlarge the loading text
              }}
            >
              Loading...
            </div>
          ) : (
            <Alert severity="warning">
              Sorry for the inconvenience, but sales have been recorded. Please
              try a different period or agent, or check back later
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
}
